import "./tabsHomePage.css"
import React, { useState, useEffect, useContext, useCallback, useMemo, Suspense } from 'react'
import { useSelector, useDispatch } from "react-redux"
import { Routes, Route, useNavigate } from 'react-router-dom'
import { TabContext, TabList } from '@mui/lab'
import { Tab, Grid, IconButton, useMediaQuery, Avatar, Chip, Tooltip } from '@mui/material'
import { Close } from '@mui/icons-material'
import ScrollToTop from "react-scroll-to-top"
import { useTranslation } from "react-i18next"
import { LoadingContext, NavigateContext, M_StateActions, M_Button, LoadingBar } from "../M_Components"
import menuObject from "./menuObject"
import ChangeLogPage from "src/pages/changeLogPage"
import M_LateralMenu from "../M_Components/M_LateralMenu/M_LateralMenu"
import { UserConfigCrud } from "src/pages/userConfigCrud"
import { SettingsPage } from "../settings/SettingsPage"
import { RoleConfigCrud } from "src/pages/roleConfigCrud"
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import RoutesMenu from "./routesMenu"

export default function TabsHomePage({ roles }) {
    const { t, i18n } = useTranslation()
    const navigate = useNavigate()
    const ctx = useContext(LoadingContext)
    const changeTabsColor = useMediaQuery('(max-width:600px)')
    const [showMenu, setShowMenu] = useState(false)
    const dispatch = useDispatch()
    const M_StateData = useSelector((state) => state.StateKeyValue)
    const [menus, setMenus] = useState([])
    const [accountName, setAccountName] = useState("")
    const [accountCompany, setAccountCompany] = useState("")

    //tabs
    const [selectedTab, setSelectedTab] = useState('menu')
    const [lastOpenTab, setLastOpenTab] = useState('')
    const [tabs, setTabs] = useState([])
    const [tabIndex, setTabIndex] = useState(1)
    const [showButtons, setShowButtons] = useState(true)
    const [isFirstTime, setIsFirstTime] = useState(true)
    const [hydrated, setHydrated] = useState(false)

    const mobileWidth = useMediaQuery('(min-width:500px)')

    // eslint-disable-next-line
    useEffect(() => {
        const name = localStorage.getItem(`accountName`)
        const company = localStorage.getItem(`accountCompany`)
        if (name !== null && company !== null) {
            setAccountName(name)
            setAccountCompany(company)
        }
    }, [])

    useEffect(() => {
        const tbs = M_StateData.filter(obj => obj.key === 'tabs')[0]?.value
        const sltbs = M_StateData.filter(obj => obj.key === 'selectedTab')[0]?.value
        const swbts = M_StateData.filter(obj => obj.key === 'showButtons')[0]?.value
        const tabIndx = M_StateData.filter(obj => obj.key === 'tabIndex')[0]?.value
        const lOT = M_StateData.filter(obj => obj.key === 'lastOpenTab')[0]?.value

        if (tbs !== null && tbs !== undefined) setTabs(tbs)
        if (sltbs !== null && sltbs !== undefined) setSelectedTab(sltbs)
        if (swbts !== null && swbts !== undefined) setShowButtons(swbts)
        if (lOT !== null && lOT !== undefined) setLastOpenTab(lOT)
        if (tabIndx !== null && tabIndx !== undefined) setTabIndex(tabIndx)
        setHydrated(true)
        // eslint-disable-next-line
    }, [])

    const cloneMenus = (data) => data.map(menu => ({
        ...menu,
        menuOpen: false,
        subMenus: Array.isArray(menu.subMenus)
            ? menu.subMenus.map(sub => ({
                ...sub,
                subMenuOpen: false,
                subSubMenus: Array.isArray(sub.subSubMenus) ? sub.subSubMenus.map(ss => ({ ...ss })) : []
            }))
            : []
    }))

    function inSubSubMenuRolesFunc(tempSubMenus, resObj, tempRoles) {
        let tempSubSubMenus = []
        resObj.subSubMenus?.forEach(subsubMenu => {
            if (tempRoles.includes(subsubMenu.tableName.toUpperCase())) {
                tempSubSubMenus.push(subsubMenu)
            }
        })
        if (tempSubSubMenus.length > 0) {
            const newObj = {
                ...resObj,
                subSubMenus: tempSubSubMenus
            }
            tempSubMenus.push(newObj)
        }
        return tempSubMenus
    }

    const menuRoles = useCallback((data) => {
        const tempMenus = cloneMenus(data)
        const safeRoles = Array.isArray(roles) ? roles : []
        const tempRoles = safeRoles.filter(obj => obj.includes('List')).map(obj => obj.replace('List', '').toUpperCase())
        const tempMenuArray = []
        tempMenus?.forEach(obj => {
            if (obj?.subMenus && obj?.subMenus?.length > 0) {
                let tempSubMenus = []
                obj?.subMenus?.forEach(resObj => {
                    if (resObj.tableName) {
                        if (tempRoles.includes(resObj.tableName.toUpperCase())) {
                            tempSubMenus.push(resObj)
                        }
                    } else if (resObj.subSubMenus?.length > 0) {
                        tempSubMenus = [...inSubSubMenuRolesFunc(tempSubMenus, resObj, tempRoles)]
                    }
                })
                if (tempSubMenus.length > 0) {
                    obj.subMenus = tempSubMenus
                    tempMenuArray.push(obj)
                }
            } else if (tempRoles.includes(obj.tableName.toUpperCase())) {
                tempMenuArray.push(obj)
            }
        })
        return tempMenuArray
    }, [roles])

    useEffect(() => {
        if (!hydrated || !isFirstTime) return
        setMenus(menuRoles(menuObject))
        if (tabs.length === 0) {
            setSelectedTab("menu")
            navigate(`/home`)
            setShowButtons(true)
            setShowMenu(!!mobileWidth)
            setLastOpenTab('')
        } else if (selectedTab === 'menu') {
            setShowButtons(true)
            setShowMenu(!!mobileWidth)
            navigate(`/home`)
        } else {
            setShowButtons(false)
            setShowMenu(false)
            const activeTab = tabs.find(t => t.value === selectedTab) || tabs[tabs.length - 1]
            if (activeTab) navigate(`/home/${activeTab.tabUrl}`)
        }
        setIsFirstTime(false)
    }, [hydrated, isFirstTime, mobileWidth, menuRoles, navigate, selectedTab, tabs])

    useEffect(() => {
        if (isFirstTime) return
        setMenus(menuRoles(menuObject))
    }, [menuRoles, isFirstTime])

    useEffect(() => {
        if (isFirstTime) return
        if (!hydrated) return
        {
            dispatch(M_StateActions.addM_State({ key: 'tabs', value: tabs }))
            dispatch(M_StateActions.addM_State({ key: 'selectedTab', value: selectedTab }))
            dispatch(M_StateActions.addM_State({ key: 'showButtons', value: showButtons }))
            dispatch(M_StateActions.addM_State({ key: 'tabIndex', value: tabIndex }))
            dispatch(M_StateActions.addM_State({ key: 'lastOpenTab', value: lastOpenTab }))
        }
    }, [tabs, selectedTab, showButtons, tabIndex, isFirstTime, hydrated, dispatch, lastOpenTab])

    const handleLogout = useCallback(() => {
        setShowMenu(false)
        localStorage.clear()
        dispatch(M_StateActions.DeleteAll_M_State())
        navigate(`/login`)
        setLastOpenTab("")
    }, [dispatch, navigate])

    const menusRoute = useCallback(() => {
        setShowButtons(true)
        setShowMenu(true)
        setSelectedTab("menu")
        navigate(`/home`)
    }, [navigate])

    const tabsRoutes = useCallback((url) => {
        setShowMenu(false)
        setShowButtons(false)
        setLastOpenTab(`${selectedTab}`)
        navigate(`/home/${url}`)
    }, [navigate, selectedTab])

    const handleChange = (event, newValue) => {
        if (!newValue || newValue === selectedTab) return
        setSelectedTab(newValue)
        dispatch(M_StateActions.addM_State({ key: 'selectedTab', value: newValue }))
        if (newValue === 'menu') {
            menusRoute()
            return
        }
        const tab = tabs.find(t => t.value === newValue)
        if (tab) tabsRoutes(tab.tabUrl)
    }

    const goToPage = useCallback((url, passLabel, event) => {
        const label = passLabel || url.slice(url.search('/') + 1)
        const existingTab = tabs.find(t => t.tabUrl === url)
        if (existingTab) {
            setSelectedTab(existingTab.value)
            dispatch(M_StateActions.addM_State({ key: 'selectedTab', value: existingTab.value }))
            if (event?.button !== 1) {
                tabsRoutes(existingTab.tabUrl)
            }
            return
        }
        if (event?.button !== 1) {
            navigate(`${url}`)
            setShowButtons(false)
            setShowMenu(false)
            setSelectedTab(`${tabIndex}`)
        }
        const newTab = {
            value: `${tabIndex}`,
            label: label,
            tabUrl: `${url}`
        }
        setTabs(prev => [...prev, newTab])
        dispatch(M_StateActions.addM_State({ key: 'selectedTab', value: `${tabIndex}` }))
        setTabIndex(prev => prev + 1)
        setLastOpenTab(`${selectedTab}`)
        ctx.toastMessages('success', '', t("MessageTabCreated"))
    }, [dispatch, navigate, tabIndex, tabs, selectedTab, ctx, t, tabsRoutes])

    const handleTabClose = useCallback((value) => {
        ctx.setIsLoad(true)
        value = typeof value !== 'string' ? `${value}` : value
        dispatch(M_StateActions.removeM_State({ key: value }))
        const tempDbTable = M_StateData.find(obj => obj.key === `"${value}.1"`)
        const tempDbTableChild = M_StateData.find(obj => obj.key === `"${value}.2"`)
        if (tempDbTable) dispatch(M_StateActions.removeM_State({ key: `"${value}.1"` }))
        if (tempDbTableChild) dispatch(M_StateActions.removeM_State({ key: `"${value}.2"` }))

        const tabArr = tabs.filter(t => t.value !== value)
        if (tabArr.length === 0) {
            setTabs([])
            setSelectedTab('menu')
            menusRoute()

            const errorKeys = M_StateData.filter(obj => obj.key !== 'tabs' && obj.key !== 'selectedTab' && obj.key !== 'showButtons' && obj.key !== 'tabIndex' && obj.key !== 'lastOpenTab')
            errorKeys.forEach(k => {
                dispatch(M_StateActions.removeM_State({ key: k.key }))
            })

        }
        else {
            const tempTab = tabArr.find(t => t.value === lastOpenTab)
            if (tempTab) {
                if (selectedTab === value) {
                    tabsRoutes(tempTab?.tabUrl)
                    setSelectedTab(`${lastOpenTab}`)
                    setTabs(tabArr)
                } else {
                    setTabs(tabArr)
                }
            } else {
                if (selectedTab === value) {
                    setSelectedTab('menu')
                    menusRoute()
                }
                setTabs(tabArr)
            }
        }
        ctx.setIsLoad(false)
        ctx.toastMessages('success', '', t("MessageTabClosed"))
    }, [ctx, dispatch, lastOpenTab, M_StateData, menusRoute, selectedTab, t, tabs, tabsRoutes])

    const handleChangeLng = (lng) => {
        i18n.changeLanguage(lng)
        localStorage.setItem("lng", lng)
    }

    const handleSettings = () => {
        goToPage('settings', t('Settings'))
    }

    const navigateContextValue = useMemo(() => ({ newTab: goToPage, closeTab: handleTabClose }), [goToPage, handleTabClose])

    return (
        <NavigateContext.Provider value={navigateContextValue}>
            <Grid container className='divTabs' style={{ backgroundColor: `${ctx.primaryColor || "var(--color-black)"}` }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={12} md={12} lg={2}>
                        <div className="buttonsDiv">
                            <M_Button
                                none
                                noHover
                                backgroundColor={ctx.primaryColor}
                                tooltip={t("HomePage")}
                                onClick={menusRoute}
                                icon={<img style={{ width: '180px' }} src="/images/webshot3.png" alt="home" />}
                            />
                            {/* <div className="divFavoritesAndAccount"> */}
                            <Tooltip title="Menu">
                                <IconButton aria-haspopup="true" size="small" disableRipple={true} onClick={() => setShowMenu(!showMenu)} style={{ marginTop: '2px' }}>
                                    <i className="buttonFavorites pi pi-bars"></i>
                                </IconButton>
                            </Tooltip>
                        </div>
                        {/* </div> */}
                    </Grid>
                    {/* <Grid item xs={1} sm={0.5} md={0.5} lg={1} style={{ display: "flex", justifyContent: "flex-start" }}>
                    </Grid> */}
                    <Grid item xs={8.5} sm={10} md={10} lg={9} style={{ flexGrow: 1 }}>
                        <TabContext value={selectedTab}>
                            <TabList
                                scrollButtons={tabs?.length !== 0}
                                allowScrollButtonsMobile
                                TabIndicatorProps={{ style: { display: "none" } }}
                                className="tabList"
                                onChange={handleChange}
                                aria-label="Dynamic Tabs"
                                variant="scrollable"
                                value={selectedTab}
                                sx={{
                                    //normal button
                                    '& .MuiTab-root': {
                                        backgroundColor: `${ctx.primaryColor ? ctx.primaryColor : "var(--color-black)"} !important`,
                                        color: `var(--color-white) !important`,
                                        cursor: 'default',
                                        borderRadius: 'inherit !important',
                                    },
                                    '& .MuiTab-root:hover': {
                                        backgroundColor: `${ctx.primaryColor ? ctx.primaryColor : "var(--color-white)"}  !important`,
                                        filter: ' grayscale(30%)',
                                        borderRadius: '8px !important'
                                    },
                                    //selected button
                                    '& .Mui-selected': {
                                        backgroundColor: `var(--color-white) !important`,
                                        color: `${ctx.primaryColor ? ctx.primaryColor : "var(--color-black)"} !important`,
                                        borderBottom: `1px solid ${ctx.primaryColor}`,
                                    },
                                    '& .Mui-selected:hover': {
                                        backgroundColor: `var(--color-white) !important`,
                                        color: `${ctx.primaryColor ? ctx.primaryColor : "var(--color-black)"} !important`,

                                    },
                                    //scroll buttons
                                    '& .MuiTabScrollButton-root': {
                                        backgroundColor: `${ctx.primaryColor ? ctx.primaryColor : "var(--color-black)"} !important`,
                                        color: `var(--color-white) !important`
                                    },
                                    '& .MuiTabScrollButton-root.Mui-disabled': {
                                        backgroundColor: `transparent !important`,
                                        color: changeTabsColor ? `var(--color-white) !important` : `transparent !important`
                                    },
                                    //is used not to show the selected default button
                                    '& .MuiTab-root:after': {
                                        backgroundColor: `var(--color-white) !important`,
                                    },
                                    '& .Mui-selected:after': {
                                        backgroundColor: `var(--color-white) !important`,
                                    }
                                }}
                            >
                                <Tab value="menu" label="menu" disableRipple={true} className="selectedTabDefault" />
                                {tabs.map((tab) => (
                                    <Tab
                                        onMouseDown={(event) => {
                                            if (event.button === 1) {
                                                document.body.onmousedown = e => { if (e.button === 1) return false; }
                                                handleTabClose(tab.value)
                                            }
                                        }}
                                        disableRipple={true}
                                        key={tab.value}
                                        label={tab.label}
                                        value={tab.value}
                                        icon={
                                            <span onClick={(e) => { e.stopPropagation(); handleTabClose(tab.value) }}>
                                                <Close style={{ fontSize: '20px', borderRadius: '10px', cursor: 'pointer' }} />
                                            </span>
                                        }
                                        iconPosition="end"
                                    />
                                ))}
                            </TabList>
                        </TabContext>
                    </Grid>
                    <Grid item xs={2.5} sm={1.5} md={1.5} lg={1} style={{ display: "flex", justifyContent: "flex-end" }}>
                        <div className="divAccount">
                            <Chip
                                style={{ backgroundColor: 'transparent', borderRadius: '10px', color: 'var(--color-white)' }}
                                avatar={
                                    <Avatar style={{ fontWeight: 'bold', color: 'var(--color-grey-dark)', backgroundColor: 'var(--color-grey-light)' }}>
                                        {accountName.charAt(0)}
                                    </Avatar>
                                }
                                label={accountCompany}
                            />
                        </div>
                    </Grid>
                </Grid>
            </Grid>
            <M_LateralMenu
                handleChangeLng={handleChangeLng}
                handleLogout={handleLogout}
                handleSettings={handleSettings}
                showMenu={showMenu}
                setShowMenu={setShowMenu}
                menus={menus}
                setMenus={setMenus}
                bodyPage={
                    showButtons ?
                        <div style={{ width: '100vw', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <img style={{ width: 'auto', maxHeight: '300px' }} src="/images/logoWebShot.svg" alt="WebShot" />
                        </div>
                        : <></>
                }
            />

            <div className='baseLine'>
                <ScrollToTop
                    smooth
                    component={<ArrowUpwardIcon fontSize="large" style={{ color: 'var(--color-white)' }} />}
                    style={{
                        backgroundColor: `${ctx.primaryColor ? ctx.primaryColor : "var(--color-black)"} !important`,
                        display: 'flex', justifyContent: 'center', alignItems: 'center', width: '45px', height: '45px'
                    }} />
                <Suspense fallback={<LoadingBar />}>
                    <Routes>
                        <Route path='/settings' element={<SettingsPage handleTabClose={handleTabClose} />} />
                        <Route path='/viewUserHistory' element={<ChangeLogPage />} />
                        <Route path='/UserConfig' element={<UserConfigCrud roles={roles} onCreateTab={goToPage} handleTabClose={handleTabClose} toastMessages={ctx.toastMessages} />} />
                        <Route path='/RoleConfig' element={<RoleConfigCrud roles={roles} onCreateTab={goToPage} handleTabClose={handleTabClose} toastMessages={ctx.toastMessages} />} />
                    </Routes>
                    <RoutesMenu roles={roles} goToPage={goToPage} ctx={ctx} t={t} handleTabClose={handleTabClose} />
                </Suspense>
            </div>
        </NavigateContext.Provider>
    )
}